import { ConfigurationService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpMethod } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services/http-service/http-service-types';
import { Information, ParameterValue } from '@philips-internal/oswald-ui-foundation/lib/context';

import { ApplicationRoute, FoundationRoute } from '../constants';
import { Endpoint } from '../constants/endpoints';

interface Establishment {
  code: string;
  locale: string;
  name: string;
  tradingName: string;
}

interface Module {
  code: string;
  name: string;
}

interface Parameter {
  module: string;
  values: Record<string, ParameterValue>;
}

interface Profile {
  code: number;
  name: string;
}

interface Sector {
  code: number;
  name: string;
}

interface User {
  code: string;
  establishment: Establishment;
  language: string;
  locale: string;
  name: string;
  photo: string;
  profile: Profile;
  sectorCode: number;
  sectorName: string;
  username: string;
}

interface Data {
  establishments: Establishment[];
  modules: Module[];
  parameters: Parameter[];
  profiles: Profile[];
  sectors: Sector[];
  user: User;
}

const defaultModules = {
  'profile': { cdFunction: 'profile', name: 'modules.profile', code: 'profile', parameters: new Map([]) },
};
const twsDefaultModules = [defaultModules.profile];

function informationMapper(data: HttpResponse<Data>): Information {
  const { body } = data;

  if (!body) {
    throw Error('Missing body.');
  }

  const { establishments, modules, parameters, profiles, sectors, user } = body;

  modules.unshift(...twsDefaultModules);

  const getModuleParameters = (module: string): Map<string, ParameterValue> => {
    const parameter = parameters.find(
      (parameter: Parameter) => parameter.module === module
    );
    const parametersMap: Map<string, ParameterValue> = new Map();

    if (parameter?.values) {
      for (const [key, value] of Object.entries(parameter.values)) {
        parametersMap.set(key, value);
      }
    }

    return parametersMap;
  };

  return {
    establishment: {
      available: establishments.map((establishment: Establishment) => ({
        id: establishment.code,
        locale: establishment.locale,
        name: establishment.name,
        tradingName: establishment.name,
      })),
      current: {
        id: user.establishment.code,
        locale: user.establishment.locale,
        name: user.establishment.name,
        tradingName: user.establishment.name,
      },
    },
    module: {
      available: modules.map((module: Module) => {
        return {
          id: module.code,
          name: module.name,
          parameters: getModuleParameters(module.code),
        };
      }),
    },
    profile: {
      available: profiles.map((profile: Profile) => ({
        id: profile.code,
        name: profile.name,
      })),
      current: {
        id: user.profile.code,
        name: user.profile.name,
      },
    },
    sector: {
      available: sectors?.map((sector: Sector) => ({
        id: sector.code,
        name: sector.name,
      })),
      current: {
        id: user.sectorCode,
        name: user.sectorName,
      },
    },
    user: {
      id: user.code,
      image: user.photo,
      language: user.language,
      locale: user.locale,
      name: user.name,
      username: user.username,
    },
  };
}

function setup(): void {
  ConfigurationService.setAuthenticationConfiguration({
    clientId: 'pendingapproval',
    endpoints: {
      information: {
        url: Endpoint.INFORMATION,
        method: HttpMethod.GET,
      },
      refresh: {
        url: Endpoint.AUTHENTICATION,
        method: HttpMethod.POST,
      },
      revoke: {
        url: Endpoint.LOGOUT,
        method: HttpMethod.POST,
      },
    },
    inactivity: {
      time: 15 * 60 * 1000,
    },
    routes: {
      custom: [
        FoundationRoute.CUSTOM_ROUTE,
        ApplicationRoute.TERMOS_OF_USE_DETAIL,
      ],
      forgotPassword: FoundationRoute.FORGOT_PASSWORD,
      home: FoundationRoute.SPOTLIGHT,
      inactivity: FoundationRoute.INACTIVITY,
      login: FoundationRoute.LOGIN,
    },
  });

  ConfigurationService.setEndpointMappers({
    // @ts-ignore
    information: informationMapper,
  });

}

export { setup };
